'use client';

import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { X, Loader2, Copy, Check } from 'lucide-react';
import { popupStorageSet } from '@/lib/ui/popupQueue';
import { useSiteConfig } from '@/context/SiteConfigContext';
import { useAuth } from '@/context/AuthContext';
import { displayFont } from '@/lib/ui/displayFont';

/**
 * Who may see the offer popups: shoppers only — guests and signed-in
 * customers. Admins, vendors, dealers, installers and other staff roles never
 * do (owner request 2026-10-23: "Others don't need that pop-up").
 * Returns null while the sign-in check is still running, so callers wait
 * rather than flash the popup at a staff member before their role is known.
 */
export function useShopperAudience(): boolean | null {
  const { user, loading } = useAuth();
  if (loading) return null;
  return !user || user.role === 'customer';
}

/**
 * The email-for-a-discount dialog, laid out like the reference store's offer
 * popup (owner reference screenshot, 2026-10-21): a lifestyle photo on the
 * left, and a cream panel with the brand, a large serif offer, first name +
 * email, one dark button, the terms, the fine print and the phone number.
 *
 * Shared by the timed popup (EmailCapturePopup) and the exit-intent popup, so
 * the two can never look different again — the exit-intent one kept its old
 * red "Wait! Don't Leave Yet!" card after the timed one was redesigned.
 * Each caller owns WHEN it opens; this owns what it shows and the sign-up.
 */

// Photo for the left half — our category 41 image (a family under day/night
// shades), the counterpart of the lifestyle shot on the reference store's popup.
// Hidden on phones, where the form needs the room.
const POPUP_IMAGE = '/uploads/categories/categories_1785380722756_57d7713e66fe62fb.webp';

// Set after a successful sign-up from either popup, so the other one does not
// ask the same visitor again.
export const OFFER_SUBSCRIBED_KEY = 'email-offer-subscribed';

/** The live popup coupon (GET commerce/homepage-popup-coupon). */
export interface PopupCoupon {
  code: string;
  discountPercent: number | null;
  discountValue: number;
  minimumOrderValue?: number;
  validUntil?: string | null;
  usageLimitPerCustomer?: number | null;
  stackableWithOtherCoupons?: boolean;
}

interface OfferDialogProps {
  /** First headline line, e.g. "New Customer Offer!" — the offer follows it. */
  title: string;
  /** Stored on the subscriber row: which popup signed them up. */
  source: 'popup' | 'exit_intent';
  coupon: PopupCoupon | null;
  /** Admin popup settings, used only when no coupon could be loaded. */
  fallbackPercent: number;
  fallbackCode: string;
  onClose: () => void;
  /** Called once the sign-up succeeds (the caller records its own "seen" key). */
  onSubscribed?: () => void;
}

export default function OfferDialog({
  title, source, coupon, fallbackPercent, fallbackCode, onClose, onSubscribed,
}: OfferDialogProps) {
  const siteConfig = useSiteConfig();
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  // Honeypot — hidden from humans; bots that fill every field are rejected
  // server-side. Stays '' for real users.
  const [honeypot, setHoneypot] = useState('');
  // Time-to-submit: when the form became usable. Sent as elapsed ms (client
  // clock only) so the server can reject instant, scripted submissions.
  const formStartRef = useRef(0);
  useEffect(() => { formStartRef.current = Date.now(); }, []);
  const [issuedCode, setIssuedCode] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  // Escape closes it, like any dialog.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Show the code and offer the subscribe call / live coupon actually use; the
  // admin popup settings are only the fallback.
  const code = issuedCode || coupon?.code || fallbackCode;
  const percent = coupon?.discountPercent ?? fallbackPercent;
  const offer = coupon && coupon.discountPercent == null ? `$${coupon.discountValue} Off` : `${percent}% Off`;
  const finePrint = [
    coupon?.minimumOrderValue ? `On orders of $${coupon.minimumOrderValue}+.` : null,
    coupon?.usageLimitPerCustomer === 1 ? 'One use per customer.' : null,
    coupon && coupon.stackableWithOtherCoupons === false ? "Can't be combined with other coupon codes." : null,
    // One promotion per order (CommerceHandler.priceCart).
    'Not valid on items already at a sale price or with the multi-window discount; one promotion per order.',
    coupon?.validUntil
      ? `Expires ${new Date(coupon.validUntil).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}.`
      : null,
    'Enter the code in your cart.',
  ].filter(Boolean).join(' ');
  const phone = siteConfig.phone || '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const trimmed = email.trim();
    if (!trimmed) { setError('Please enter your email address'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) { setError('Please enter a valid email address'); return; }

    setIsSubmitting(true);
    try {
      const response = await fetch('/api/email-subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: trimmed,
          firstName: firstName.trim(),
          source,
          couponCode: code,
          company_website: honeypot, // honeypot — always '' for real users
          formElapsedMs: formStartRef.current ? Date.now() - formStartRef.current : undefined,
        }),
      });
      const result = await response.json();
      if (response.ok && result.success) {
        setIssuedCode(result.couponCode || null);
        setIsSubmitted(true);
        // Email for abandoned-cart tracking, and "signed up" so neither popup
        // asks again. Mirrored store: raw localStorage throws with site data
        // blocked.
        popupStorageSet('guest_email', trimmed);
        popupStorageSet(OFFER_SUBSCRIBED_KEY, Date.now().toString());
        onSubscribed?.();
      } else {
        setError(result.error || 'Something went wrong. Please try again.');
      }
    } catch {
      setError('Failed to subscribe. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      // Older browsers
      const textArea = document.createElement('textarea');
      textArea.value = code;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const pill = 'w-full h-12 rounded-full border border-gray-800 bg-white px-5 text-base text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-900 disabled:opacity-60';
  const darkButton = 'w-full h-12 rounded-full bg-gray-900 text-white text-base font-medium hover:bg-gray-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2';
  const titleId = `${source}-offer-title`;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-[9998]" onClick={onClose} />

      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-6 pointer-events-none">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          className="pointer-events-auto relative w-full max-w-[1000px] max-h-[calc(100vh-24px)] overflow-hidden rounded-lg bg-sb-bg-alt shadow-2xl md:grid md:grid-cols-[52%_48%] animate-in zoom-in-95 duration-300"
        >
          {/* Photo */}
          <div className="relative hidden md:block min-h-[560px]">
            <Image src={POPUP_IMAGE} alt="" fill sizes="520px" className="object-cover" priority />
          </div>

          {/* Close */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 z-10 inline-flex h-11 w-11 items-center justify-center rounded-full border border-gray-400 bg-white/90 text-gray-700 hover:bg-white"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="max-h-[calc(100vh-24px)] overflow-y-auto px-6 py-8 sm:px-10 sm:py-10 text-center flex flex-col justify-center">
            {/* Brand */}
            <div className="flex items-center justify-center gap-2">
              <Image src="/images/logo/logo-charcoal-v3.png" alt="" width={32} height={32} className="rounded" />
              <span className={`${displayFont.className} text-2xl text-heading-charcoal`}>{siteConfig.companyName}</span>
            </div>

            {!isSubmitted ? (
              <>
                <h2 id={titleId} className={`${displayFont.className} mt-6 text-[32px] sm:text-[40px] leading-[1.1] text-heading-charcoal`}>
                  {title}
                  <br />
                  Get {offer}**!
                </h2>
                <p className="mt-4 text-base text-gray-800">
                  Sign up for our email list* and get your code right away. Plus, get exclusive deals and design inspiration!
                </p>

                <form onSubmit={handleSubmit} className="mt-6 space-y-3 text-left">
                  {/* Honeypot — invisible to humans, irresistible to form-filling bots. */}
                  <div aria-hidden="true" className="absolute left-[-9999px] top-auto h-px w-px overflow-hidden">
                    <label htmlFor={`${source}-company-website`}>Company website (leave blank)</label>
                    <input
                      maxLength={80}
                      id={`${source}-company-website`}
                      name="company_website"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={honeypot}
                      onChange={(e) => setHoneypot(e.target.value)}
                    />
                  </div>
                  <label htmlFor={`${source}-offer-first`} className="sr-only">First name</label>
                  <input
                    id={`${source}-offer-first`}
                    type="text"
                    maxLength={100}
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="First Name*"
                    required
                    autoComplete="given-name"
                    disabled={isSubmitting}
                    className={pill}
                  />
                  <label htmlFor={`${source}-offer-email`} className="sr-only">Email address</label>
                  <input
                    id={`${source}-offer-email`}
                    type="email"
                    maxLength={254}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email Address*"
                    autoComplete="email"
                    disabled={isSubmitting}
                    className={pill}
                  />
                  {error && <p role="alert" className="text-sm text-red-700 text-center">{error}</p>}
                  <button type="submit" disabled={isSubmitting} className={`mt-2 ${darkButton}`}>
                    {isSubmitting ? (<><Loader2 className="h-5 w-5 animate-spin" />Signing up…</>) : `Get My ${offer}`}
                  </button>
                </form>

                <p className="mt-4 text-xs text-gray-700">
                  *By submitting this form, you agree to receive marketing emails. Unsubscribe anytime. See our{' '}
                  <Link href="/terms" className="font-semibold text-primary-red underline">Terms of Service</Link> and{' '}
                  <Link href="/privacy" className="font-semibold text-primary-red underline">Privacy Policy</Link>.
                </p>
                <p className="mt-3 text-xs text-gray-700">**{finePrint}</p>
              </>
            ) : (
              <>
                <h2 id={titleId} className={`${displayFont.className} mt-6 text-[32px] sm:text-[40px] leading-[1.1] text-heading-charcoal`}>
                  You&apos;re in!
                </h2>
                <p className="mt-4 text-base text-gray-800">Here&apos;s your {offer} code:</p>
                <button
                  type="button"
                  onClick={copyCode}
                  className="mt-4 mx-auto inline-flex items-center gap-3 rounded-full border-2 border-dashed border-gray-900 bg-white px-7 py-3 text-2xl font-bold tracking-widest text-gray-900"
                  aria-label={`Copy code ${code}`}
                >
                  {code}
                  {copied ? <Check className="h-5 w-5 text-green-700" /> : <Copy className="h-5 w-5" />}
                </button>
                <p className="mt-2 text-sm text-gray-700">{copied ? 'Copied!' : 'Tap to copy'}</p>
                <button onClick={onClose} className={`mt-6 ${darkButton}`}>Start Shopping</button>
                <p className="mt-4 text-xs text-gray-700">**{finePrint}</p>
              </>
            )}

            {phone && (
              <p className="mt-5 text-sm text-gray-800">
                Need help? Call us:{' '}
                <a href={`tel:${phone.replace(/[^0-9]/g, '')}`} className="font-medium hover:underline">{phone}</a>
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
